import { useFormDialogContext } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import { RaRecord, useNotify, useRefresh } from 'react-admin';
import { FieldValues } from 'react-hook-form';

import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import { PostB2bRecurringInvestmentsEndpoint } from '@Common/types/bo/b2b/recurring_investments';

import { useCreatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

type RecurringInvestmentBody = PostB2bRecurringInvestmentsEndpoint['Client']['RequestBody'];
type RecurringInvestmentResponse = PostB2bRecurringInvestmentsEndpoint['Client']['ResponseBody'];

/**
 * A recurring investment targets a single plan: the form rows are grouped by plan (and
 * deductibility), one recurring investment being created per group.
 * Fully managed plans carry a single total, libre plans carry their instrument allocations.
 */
export const buildRecurringInvestmentBodies = (data: FieldValues): RecurringInvestmentBody[] => {
  const bodies = new Map<string, RecurringInvestmentBody>();

  for (const row of data.rows ?? []) {
    if (!row.saving_plan_id || !row.amount) {
      continue;
    }

    const key = `${row.saving_plan_id}-${row.deductibility || 'none'}`;
    const common = {
      b2b_employee_id: data.b2b_employee_id,
      b2b_company_plan_id: row.saving_plan_id,
      payment_method: 'sepa-direct-debit' as const,
      deductibility: row.deductibility || undefined,
      start_date: data.start_date,
      frequency: data.frequency,
      payer: data.payer,
    };

    if (row.plan_data && isFullyManagedPlan(row.plan_data.type)) {
      bodies.set(key, { ...common, user_currency_total_value: row.amount });
      continue;
    }

    const existing = bodies.get(key);
    const allocation = { instrument_id: row.instrument_id, amount: row.amount };
    if (existing && 'allocations' in existing) {
      existing.allocations.push(allocation);
    } else {
      bodies.set(key, { ...common, allocations: [allocation] });
    }
  }

  return Array.from(bodies.values());
};

export const useRecurringInvestmentCreate = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { close } = useFormDialogContext();
  const [createRecurringInvestment] = useCreatePromise<
    RecurringInvestmentBody,
    RecurringInvestmentResponse & RaRecord
  >();

  return useCallback(
    async (values: FieldValues) => {
      try {
        await Promise.all(
          buildRecurringInvestmentBodies(values).map(async (body) =>
            createRecurringInvestment('b2b/recurring_investments', { data: body }),
          ),
        );
      } catch (error) {
        // Other groups may have been created before the failure
        refresh();
        notify('resources.bo_common.b2b.recurring_investments.create.actions.failure', {
          type: 'error',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
        return;
      }
      close();
      notify('resources.bo_common.b2b.recurring_investments.create.actions.success', {
        type: 'success',
      });
      refresh();
    },
    [createRecurringInvestment, notify, refresh, close],
  );
};
